import {
  Box,
  FormControl,
  FormControlProps,
  FormHelperText,
  InputBaseProps,
  InputLabel,
} from "@mui/material";
import { AxiosResponse } from "axios";
import { useEffect, useRef, useState } from "react";
import {
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  UseControllerProps,
  UseFormClearErrors,
  UseFormGetFieldState,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import { useQueries } from "react-query";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import UploadProgressBar from "src/components/common/UploadProgressBar";
import VideoPreviewDialog from "src/components/common/VideoPreviewDialog";
import errorMessage from "src/constants/errorMessage";
import useFileUpload from "src/hook/useFileUpload";
import { CustomStyles, getStyles } from "src/styles/theme";
import { getImageById } from "src/utils/api/dashboard";
import { getError } from "src/utils/common";
import { Button } from "../Buttons";
import defaultStyles, { StylesClasses } from "./styles";
import { styled } from "@mui/material/styles";

type FileUploadProps<T> = UseControllerProps<T> &
  FormControlProps &
  InputBaseProps & {
    errors?: FieldErrors;
    customStyles?: CustomStyles<StylesClasses>;
    label?: string;
    multiple?: boolean;
    maxLimit?: number;
    version?: "standard" | "lite";
    setValue: UseFormSetValue<T>;
    getValues: UseFormGetValues<T>;
    setError: UseFormSetError<T>;
    clearErrors: UseFormClearErrors<T>;
    defaultFiles?: { key: string; file: string }[];
    accept?: string;
    maxFileSize?: number;
    loading?: boolean;
    dragDropHeading?: string;
    supportedFormatMessage?: string;
    trigger?: UseFormTrigger<T>;
    getFieldState?: UseFormGetFieldState<T>;
    hideUpload?: boolean;
    dragDropText?: string;
    onSuccess?: (res: AxiosResponse) => void;
    reqOcr?: boolean;
    required?: boolean;
    imageType?: string;
    disabled?: boolean;
    uploadMetaData?:
      | {
          documentType: string;
          name?: string;
          type?: string;
          raw: boolean;
          dob?: string;
          docId?: string;
          nationality?: string;
        }
      | {};
    // formState?: UseFormStateProps<T>; // isDirty?: UseForm
  };

const FileUpload = <T extends FieldValues>({
  name,
  setValue,
  getValues,
  setError,
  clearErrors,
  label,
  control,
  fullWidth = true,
  errors,
  maxLimit = 5,
  multiple = false,
  version = "standard",
  rules,
  className,
  maxFileSize = 10485760,
  customStyles,
  accept = "image/jpeg, image/png, image/webp, image/jpg, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document,  .doc, .docx, .xml, application/vnd.google-earth.kml+xml, .kml, .csv, .xlsx, .xls",
  supportedFormatMessage = "Supported formats: images, pdf and Docs Size Upto 10MB",
  loading,
  disabled = false,
  defaultFiles = [],
  trigger,
  required = true,
  hideUpload = false,
  dragDropText = "Drag Drop your file here",
  getFieldState,
  uploadMetaData = {},
  dragDropHeading = "",
  reqOcr = false,
  imageType = "default",
  onSuccess,
  ...rest
}: FileUploadProps<T>) => {
  const ref = useRef(null);
  const [files, setFile] = useState<any>(getValues(name) || []);
  const [previewVideo, setPreviewVideo] = useState<{
    src: string;
    title: string;
  } | null>(null);
  const error = getError(name, errors);
  const styles = getStyles<StylesClasses>(defaultStyles, customStyles);

  const result = useQueries(
    defaultFiles.map((file) => ({
      queryKey: ["get", "getImage", file.key],
      queryFn: () => getImageById(file.key),
      staleTime: Infinity,
      onSuccess: (res: AxiosResponse) => {
        setFile((prev) => [
          ...prev,
          {
            type: "default",
            key: file.key,
            file: res.data.fileUrl,
            name: res.data.fileName,
          },
        ]);
      },
    }))
  );

  const {
    upload,
    progress: uploadProgress,
    isUploading,
  } = useFileUpload({
    ocr: reqOcr,
    // Functional update: a selection can hold several files, and `files` here
    // is the value captured when the upload started.
    onSuccess: (uploadedFile, res, source) => {
      setFile((prev) => [
        ...prev,
        {
          type: imageType,
          file: uploadedFile.fileUrl,
          key: uploadedFile.key,
          mimeType: source?.type,
          name: source?.name,
        },
      ]);
      !!onSuccess && onSuccess(res.data);
    },
    onError: (message) => setError(name, { type: "invalidDocument", message }),
  });

  useEffect(() => {
    (async () => {
      if (defaultFiles?.length && !files.length) {
        // defaultFiles.map((file) => getImage(file.key));
        // for await (const file of defaultFiles) {
        //   debugger;
        // /*   const data = getImage(); */
        //   console.log(data, "==============>");
        // }
        // setFile(defaultFiles.slice(0, maxLimit));
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFiles]);

  useEffect(() => {
    const file = files?.length ? files : [];
    setValue(name, [...file] as PathValue<T, Path<T> & string>);
    trigger(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const fileType = accept.split(",").map((str) => str.trim());
  const acceptsVideo = accept.includes("video");

  const isVideoItem = (item: { mimeType?: string; file?: string }) =>
    !!item.file &&
    (item.mimeType ? item.mimeType.startsWith("video/") : acceptsVideo);

  const handleError = (file: File) => {
    console.log(file.type, "This is the filetype");
    if (file.size > maxFileSize) {
      setError(name, {
        type: "sizeLimit",
        message: errorMessage.fileUpload.maxFileSize(
          maxFileSize / (1024 * 1024)
        ),
      });
      return true;
    }

    // if (!fileType?.includes(file?.type)) {
    //   setError(name, {
    //     type: "invalidFiletype",
    //     message: errorMessage.fileUpload.invalidFileType,
    //   });
    //   return true;
    // }
    clearErrors(name);
    return false;
  };

  const fileInputRef = useRef(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors(name);
    let allFiles = { ...e.target.files };
    let length = e.target.files.length;

    if (maxLimit - files.length < length) {
      setError(name, {
        type: "maxLimit",
        message: errorMessage.fileUpload.fileLimitExceed(maxLimit),
      });
      return;
    }

    for (let key in allFiles) {
      const selectedFile = allFiles[key];

      if (handleError(selectedFile)) continue;

      try {
        // Uploaded one at a time so each file reports its own progress and the
        // selection is not sent in parallel over a single uplink.
        await upload(selectedFile, reqOcr ? uploadMetaData : {});
      } catch {
        // onError has already put the reason on the field.
        break;
      }
    }
  };

  const clearImage = (files, idx) => {
    /*  let filteredFiles = files?.filter((file) => {
      return file !== files[idx];
    }); */

    let filteredFiles = files.filter((file, index) => {
      return index !== idx;
    });

    if (fileInputRef?.current?.value) fileInputRef.current.value = "";
    if (ref?.current?.value) fileInputRef.current.value = "";

    setFile(filteredFiles);
  };

  return (
    <Box {...styles("container")}>
      <Box {...styles("outerLayer")} id="scrollableDiv">
        <Box style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
          {files?.map((item, idx) => (
            <Box
              {...styles("fileUploadPreview")}
              key={idx}
              style={{ maxWidth: "400px" }}
            >
              <Box {...styles("overviewItemText")}>
                {isVideoItem(item) ? (
                  <Box
                    {...styles("videoPreviewWrapper")}
                    title="Click to play"
                    onClick={() =>
                      setPreviewVideo({
                        src: item.file,
                        title: item.name ?? item.key,
                      })
                    }
                  >
                    <Box
                      {...styles("videoPreview")}
                      component={"video"}
                      src={`${item.file}#t=0.5`}
                      preload="metadata"
                      muted
                      playsInline
                    />
                    <Box {...styles("playOverlay")} className="playOverlay">
                      <PlayCircleOutlineIcon fontSize="large" />
                    </Box>
                  </Box>
                ) : (
                  <Box
                    {...styles("receiptPreview")}
                    component={"img"}
                    src={
                      !!item.file
                        ? item.file
                        : "/assets/images/NoImageFoundIcon.png"
                    }
                    onError={(
                      e: React.SyntheticEvent<HTMLImageElement, Event>
                    ) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/assets/svg/receipt.svg";
                    }}
                    alt="file_image"
                  />
                )}

                <Box {...styles("fileName")} title={item.name ?? item.key}>
                  {item.name ?? `File ${idx + 1}`}
                </Box>
              </Box>
              <Box onClick={(e) => e.stopPropagation()} {...styles("tickIcon")}>
                <Box {...styles("imageBox")}>
                  <Box
                    component={"img"}
                    src="/assets/images/fileUploadSuccessIcon.png"
                    alt="tick_icon"
                  />

                  <Box {...styles("uploaded")}>Uploaded</Box>
                </Box>

                {!disabled && (
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      clearImage(files, idx);
                    }}
                    {...styles("crossIcon")}
                    component={"img"}
                    src="/assets/images/fileUploadCrossIcon.png"
                    alt="remove_icon"
                  />
                )}
              </Box>
            </Box>
          ))}
          <Box style={{ width: "100%" }}>
            <Controller
              render={({ fieldState }) => (
                <>
                  {label && (
                    <InputLabel
                      shrink
                      {...styles(["label"])}
                      required={!!rules?.required || required}
                      htmlFor={name}
                      disabled={disabled}
                    >
                      {label}
                    </InputLabel>
                  )}
                  {files?.length < maxLimit && !hideUpload && (
                    <FormControl
                      {...styles("wrapper", {
                        borderColor: "custom.border.otpDash",
                        /*    fieldState.invalid || getError(name, errors)
                          ? "error.main"
                          : "custom.border.otpDash", */
                      })}
                      fullWidth={fullWidth}
                      className={className}
                      required={!!rules?.required}
                    >
                      <Box
                        disabled={disabled}
                        // multiple={multiple}
                        component="input"
                        type="file"
                        accept={accept}
                        value=""
                        onChange={handleFileUpload}
                        ref={fileInputRef}
                        {...styles("inputWrapper")}
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          // e.preventDefault();
                        }}
                      />
                      <Box className="inputButton" />

                      <Box
                        onClick={() => {
                          fileInputRef.current.click();
                        }}
                        {...styles(
                          version === "standard" ? "contentStandard" : "content"
                        )}
                      >
                        <Box {...styles("dragAndDropArea")}>
                          <Box {...styles("dragArea")}>
                            <Box
                              {...(styles("dragText"),
                              {
                                color:
                                  disabled === true
                                    ? "custom.disabled.btn"
                                    : "custom.text.dark",
                              })}
                            >
                              {dragDropText}&nbsp;
                              <Box
                                component="span"
                                {...styles("dragDropHeading")}
                              >
                                {dragDropHeading}
                              </Box>
                            </Box>
                          </Box>
                          <Box {...styles("uploadFile")}>
                            {version === "standard" && (
                              <Box
                                {...(styles("fileType"),
                                {
                                  color:
                                    disabled === true
                                      ? "custom.disabled.btn"
                                      : "custom.label",
                                })}
                              >
                                {supportedFormatMessage}
                              </Box>
                            )}
                          </Box>
                          <Button
                            loading={isUploading}
                            disabled={disabled}
                            // disabled={isLoading}
                            variant="outlined"
                            text="Upload File"
                            customStyles={{
                              root: defaultStyles.btn,
                              circle: { color: "primary.main" },
                            }}
                          />

                          <UploadProgressBar
                            active={isUploading}
                            progress={uploadProgress}
                          />
                        </Box>
                      </Box>
                      {!!error && (
                        <FormHelperText {...styles("errorMsg")}>
                          {error?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}
              name={name}
              control={control}
              rules={rules}
              {...rest}
            />
          </Box>
        </Box>
      </Box>

      {!!previewVideo && (
        <VideoPreviewDialog
          open
          onClose={() => setPreviewVideo(null)}
          src={previewVideo.src}
          title={previewVideo.title}
        />
      )}
    </Box>
  );
};

export default FileUpload;
